// CPC ↔ BSC USDT cross-chain bridge (UI + CPC lock; BSC USDT escrow M2)
import { useCurrentAccount, useSuiClient } from '@mysten/dapp-kit';
import {
    BRIDGE_CHAIN_ID_BNB,
    BRIDGE_CHAIN_ID_BNB_TESTNET,
    buildLockForBnbForOwner,
    fetchBridgeLockedEvents,
    fetchBridgeTreasuryMist,
    getCoinBalanceView,
    mistToDgc,
    parseEvmAddressToBytes,
    verifyBridgeObjectsReachable,
    type BridgeLockedEventRow,
} from '@cpc/tx';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Heading, Text } from '@mysten/ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';

import { BscConnectButton } from '~/components/bridge/BscConnectButton';
import { PageLayout } from '~/components/Layout/PageLayout';
import { CpcPageHero, cpcPageContentClass } from '~/components/cpc/CpcPageHero';
import { useT } from '~/i18n/LocaleContext';
import { Card } from '~/ui/Card';
import {
    cpcAmountToUsdt,
    getBridgeCpcUsdtRate,
    getBridgeMarketConfig,
    getBscBridgeContract,
    getBscRpcUrlOrDefault,
    getBscUsdtDecimals,
    getBscUsdtEscrowContract,
    getBscUsdtTokenAddress,
    getDefaultBscRecipientAddress,
    getDefaultCpcRecipientAddress,
    isValidBridgeAmount,
    sanitizeBridgeAmountInput,
    usdtAmountToCpc,
} from '~/config/bridgeConfig';
import { useBscWallet } from '~/hooks/useBscWallet';
import { useCpcSignAndExecuteTransaction } from '~/hooks/useCpcSignAndExecuteTransaction';
import { lockUsdtForCpcOnBsc } from '~/utils/bscEscrowTx';
import { tryFormatCpcAddress } from '~/utils/cpcAddress';
import { getDataCoinType } from '~/config/clobConfig';

/** Bridge asset on DGC chain (product: DATA ↔ BNB USDT @ 1:1; not gas coin DGC). */
const BRIDGE_ASSET_SYMBOL = 'DATA';
/** BSC mainnet Binance-Peg USDT display name */
const USDT_SYMBOL = 'BNB USDT';

function toCpcDisplayAddress(addr: string | undefined | null): string {
    const s = (addr ?? '').trim();
    return s ? tryFormatCpcAddress(s) : '';
}

type BridgeDirection = 'usdt-to-cpc' | 'cpc-to-usdt';

function formatTime(ms: number | null): string {
    if (!ms) return '—';
    return new Date(ms).toLocaleString();
}

// 方向标签组件 - 移动端优化
function DirectionTabs({
    direction,
    onChange,
}: {
    direction: BridgeDirection;
    onChange: (d: BridgeDirection) => void;
}) {
    const tabs: { id: BridgeDirection; label: string }[] = [
        { id: 'usdt-to-cpc', label: `${USDT_SYMBOL} → DATA` },
        { id: 'cpc-to-usdt', label: `DATA → ${USDT_SYMBOL}` },
    ];
    return (
        <div className="flex p-1 bg-white/5 rounded-xl border border-white/10 w-full sm:max-w-xs">
            {tabs.map((t) => (
                <button
                    key={t.id}
                    type="button"
                    onClick={() => onChange(t.id)}
                    className={clsx(
                        'flex-1 py-2.5 px-2 text-center text-sm font-medium rounded-lg transition-all duration-200 touch-manipulation',
                        'min-h-[44px]', // 移动端触摸友好
                        direction === t.id
                            ? 'bg-gradient-to-r from-[#38BDF8] to-[#3B82F6] text-white shadow-lg shadow-blue-500/25'
                            : 'text-gray-400 hover:text-white hover:bg-white/5 active:bg-white/10',
                    )}
                >
                    {t.label}
                </button>
            ))}
        </div>
    );
}

// 数字输入框组件 - 移动端优化
function AmountInput({
    value,
    onChange,
    label,
    symbol,
    balance,
    balanceLabel,
    disabled = false,
}: {
    value: string;
    onChange: (v: string) => void;
    label: string;
    symbol: string;
    balance?: string | number | null;
    balanceLabel?: string;
    disabled?: boolean;
}) {
    const t = useT();
    return (
        <div className="bg-[#0F172A]/80 backdrop-blur-sm border border-white/10 rounded-2xl p-4 transition-all focus-within:border-[#38BDF8]/50 focus-within:shadow-[0_0_30px_rgba(56,189,248,0.05)]">
            <div className="flex justify-between items-center mb-2">
                <span className="text-xs text-gray-400 font-medium">{label}</span>
                {balance != null && (
                    <span className="text-xs text-gray-500 tabular-nums">
                        {balanceLabel || t('bridge.balance')}: {typeof balance === 'number' ? balance.toFixed(4) : balance}
                    </span>
                )}
            </div>
            <div className="flex items-center gap-3">
                <input
                    type="text"
                    className="w-full bg-transparent p-0 text-2xl sm:text-3xl font-semibold text-white focus:ring-0 placeholder-gray-600 tabular-nums 
                             [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none
                             min-h-[44px] touch-manipulation"
                    value={value}
                    onChange={(e) => onChange(disabled ? value : sanitizeBridgeAmountInput(e.target.value))}
                    placeholder="0.00"
                    disabled={disabled}
                    inputMode="decimal"
                    autoComplete="off"
                />
                <div className="flex items-center gap-2 bg-white/5 px-3 py-1.5 rounded-xl border border-white/5 shrink-0">
                    <span className="text-sm font-bold tracking-wide text-gray-300">{symbol}</span>
                </div>
            </div>
        </div>
    );
}

// 收款地址输入组件
function RecipientInput({
    value,
    onChange,
    label,
    placeholder,
    onUseCurrent,
    currentLabel,
    disabled = false,
}: {
    value: string;
    onChange: (v: string) => void;
    label: string;
    placeholder: string;
    onUseCurrent?: () => void;
    currentLabel?: string;
    disabled?: boolean;
}) {
    const t = useT();
    return (
        <div className="space-y-1.5 pt-1">
            <label className="text-xs font-medium text-gray-400">{label}</label>
            <div className="relative">
                <input
                    className="w-full rounded-xl bg-[#0F172A]/80 backdrop-blur-sm border border-white/10 px-4 py-3 
                               font-mono text-sm sm:text-base text-gray-200 focus:border-[#38BDF8] focus:ring-0 
                               placeholder-gray-600 transition-all min-h-[48px] touch-manipulation"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    disabled={disabled}
                    spellCheck={false}
                />
                {onUseCurrent && (
                    <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-[#38BDF8] 
                                   hover:text-blue-400 transition active:scale-95 touch-manipulation
                                   bg-[#0F172A] px-2 py-1 rounded-lg border border-white/5"
                        onClick={onUseCurrent}
                    >
                        {currentLabel || t('bridge.useCurrent')}
                    </button>
                )}
            </div>
        </div>
    );
}

// 主按钮组件
function ActionButton({
    onClick,
    disabled,
    loading,
    loadingText,
    children,
    variant = 'primary',
}: {
    onClick: () => void;
    disabled?: boolean;
    loading?: boolean;
    loadingText?: string;
    children: React.ReactNode;
    variant?: 'primary' | 'secondary';
}) {
    const t = useT();
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled || loading}
            className={clsx(
                'w-full mt-3 rounded-xl px-4 py-3.5 sm:py-4 text-base font-bold text-white',
                'transition-all duration-200 touch-manipulation min-h-[52px]',
                'active:scale-[0.98] active:duration-100',
                variant === 'primary'
                    ? 'bg-gradient-to-r from-[#38BDF8] to-[#3B82F6] hover:shadow-lg hover:shadow-blue-500/25 disabled:from-[#2a4a6a] disabled:to-[#2a4a6a]'
                    : 'bg-[#1E293B] hover:bg-[#2D3A4F] disabled:bg-[#1a1f2e]',
                (disabled || loading) && 'opacity-60 cursor-not-allowed active:scale-100',
            )}
        >
            {loading ? (
                <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {loadingText || t('bridge.processing')}
                </span>
            ) : (
                children
            )}
        </button>
    );
}

// 状态指示器组件
function StatusBadge({ ok, loading, label }: { ok?: boolean; loading?: boolean; label: string }) {
    const t = useT();
    let statusText = label;
    let colorClass = 'text-gray-500';

    if (loading) {
        statusText = `${t('bridge.detecting')}…`;
        colorClass = 'text-yellow-400';
    } else if (ok === true) {
        statusText = `✓ ${t('bridge.ok')}`;
        colorClass = 'text-emerald-400';
    } else if (ok === false) {
        statusText = `✗ ${t('bridge.fail')}`;
        colorClass = 'text-rose-400';
    }

    return <span className={clsx('font-semibold', colorClass)}>{statusText}</span>;
}

// 信息行组件
function InfoRow({ label, value, valueClassName }: { label: string; value: React.ReactNode; valueClassName?: string }) {
    return (
        <div className="flex justify-between items-center gap-2 py-1.5 border-b border-white/5 last:border-0">
            <span className="text-xs text-gray-400 shrink-0">{label}</span>
            <span className={clsx('text-xs text-gray-300 truncate', valueClassName)}>{value}</span>
        </div>
    );
}

export default function CpcBridgePage() {
    const t = useT();
    const navigate = useNavigate();
    const config = useMemo(() => getBridgeMarketConfig(), []);
    const dataTypeTag = useMemo(() => getDataCoinType(), []);
    const rate = getBridgeCpcUsdtRate();
    const usdtDecimals = getBscUsdtDecimals();
    const defaultBscRecipient = getDefaultBscRecipientAddress();
    const defaultCpcRecipient = getDefaultCpcRecipientAddress();

    const client = useSuiClient();
    const cpcAccount = useCurrentAccount();
    const queryClient = useQueryClient();
    const { mutateAsync: signAndExecute, isPending: submitting } =
        useCpcSignAndExecuteTransaction();

    const [direction, setDirection] = useState<BridgeDirection>('usdt-to-cpc');
    const [useTestnet, setUseTestnet] = useState(false);
    const [amountUsdt, setAmountUsdt] = useState('');
    const [amountCpc, setAmountCpc] = useState('');
    const [lockingBsc, setLockingBsc] = useState(false);
    const [cpcRecipient, setCpcRecipient] = useState(() => toCpcDisplayAddress(defaultCpcRecipient));
    const [bscAddress, setBscAddress] = useState(defaultBscRecipient);

    const bscRpc = useTestnet
        ? 'https://data-seed-prebsc-1-s1.bnbchain.org:8545'
        : getBscRpcUrlOrDefault();
    const usdtToken = getBscUsdtTokenAddress(useTestnet);
    const escrowContract = getBscUsdtEscrowContract();
    const legacyBridge = getBscBridgeContract();

    const bscWallet = useBscWallet({
        useTestnet,
        usdtToken,
        usdtDecimals,
        bscRpcUrl: bscRpc,
    });

    const chainId = useTestnet ? BRIDGE_CHAIN_ID_BNB_TESTNET : BRIDGE_CHAIN_ID_BNB;
    const chainLabel = useTestnet ? 'BSC Chapel (97)' : 'BNB Smart Chain (56)';
    const rateLabel = `1 ${BRIDGE_ASSET_SYMBOL} ≈ ${rate} ${USDT_SYMBOL}`;

    const estimatedCpc = useMemo(() => usdtAmountToCpc(amountUsdt, rate), [amountUsdt, rate]);
    const estimatedUsdt = useMemo(() => cpcAmountToUsdt(amountCpc, rate), [amountCpc, rate]);

    useEffect(() => {
        if (bscWallet.address) setBscAddress(bscWallet.address);
        else setBscAddress(defaultBscRecipient);
    }, [bscWallet.address, defaultBscRecipient]);

    useEffect(() => {
        if (cpcAccount?.address) setCpcRecipient(toCpcDisplayAddress(cpcAccount.address));
        else setCpcRecipient(toCpcDisplayAddress(defaultCpcRecipient));
    }, [cpcAccount?.address, defaultCpcRecipient]);

    const statusQ = useQuery({
        queryKey: ['cpc-bridge-status', config?.bridgeId],
        enabled: !!config,
        queryFn: async () => {
            if (!config) throw new Error('no config');
            const ok = await verifyBridgeObjectsReachable(client, config);
            const treasury = await fetchBridgeTreasuryMist(client, config.bridgeId);
            return { ok, treasury };
        },
        refetchInterval: 15_000,
    });

    const eventsQ = useQuery({
        queryKey: ['cpc-bridge-locked', config?.packageId, String(chainId)],
        enabled: !!config,
        queryFn: async () => {
            if (!config) return [];
            return fetchBridgeLockedEvents(client, config.packageId, {
                chainId,
                limit: 15,
            });
        },
        refetchInterval: 10_000,
    });

    const dataBalQ = useQuery({
        queryKey: ['cpc-bridge-data-bal', cpcAccount?.address, dataTypeTag],
        enabled: !!cpcAccount?.address && !!dataTypeTag,
        queryFn: async () => {
            if (!cpcAccount?.address) return null;
            return getCoinBalanceView(client, cpcAccount.address, dataTypeTag);
        },
        refetchInterval: 15_000,
    });

    const goHistory = useCallback(
        (addr?: string) => {
            const a = (addr || cpcAccount?.address || '').trim();
            navigate(a ? `/bridge/history?address=${encodeURIComponent(a)}` : '/bridge/history');
        },
        [cpcAccount?.address, navigate],
    );

    const refresh = useCallback(() => {
        void queryClient.invalidateQueries({ queryKey: ['cpc-bridge-status'] });
        void queryClient.invalidateQueries({ queryKey: ['cpc-bridge-locked'] });
        void queryClient.invalidateQueries({ queryKey: ['cpc-bridge-data-bal'] });
        void queryClient.invalidateQueries({ queryKey: ['cpc-bridge-history'] });
        void bscWallet.refreshUsdtBalance();
    }, [queryClient, bscWallet]);

    const onLockCpcForUsdt = async () => {
        if (!config || !cpcAccount) return;
        if (!amountCpc.trim()) {
            toast.error(t('bridge.needAmount'));
            return;
        }
        if (!isValidBridgeAmount(amountCpc)) {
            toast.error(t('bridge.minAmount'));
            return;
        }
        const recipientAddr = bscAddress.trim();
        if (!recipientAddr) {
            toast.error(t('bridge.needBscRecipient'));
            return;
        }
        try {
            const recipient = parseEvmAddressToBytes(recipientAddr);
            const tx = await buildLockForBnbForOwner(client, cpcAccount.address, {
                packageId: config.packageId,
                bridgeId: config.bridgeId,
                amountCpc,
                evmRecipientBytes: recipient,
                useTestnet,
                dataTypeTag,
            });
            tx.setSenderIfNotSet(cpcAccount.address);
            await signAndExecute({ transaction: tx });
            toast.success(
                t('bridge.lockedDataOk')
                    .replace('{amount}', amountCpc)
                    .replace('{data}', BRIDGE_ASSET_SYMBOL)
                    .replace('{recipient}', recipientAddr.slice(0, 10))
                    .replace('{usdtAmount}', estimatedUsdt)
                    .replace('{usdt}', USDT_SYMBOL),
            );
            refresh();
            goHistory(cpcAccount.address);
        } catch (e) {
            toast.error(e instanceof Error ? e.message : String(e));
        }
    };

    const onLockUsdtForCpc = async () => {
        if (!bscWallet.address) {
            toast.error(t('bridge.connectBscFirst'));
            return;
        }
        if (!cpcRecipient.trim()) {
            toast.error(t('bridge.needCpcRecipient'));
            return;
        }
        if (!escrowContract) {
            toast.error(t('bridge.escrowDeployHint'));
            return;
        }
        if (useTestnet) {
            toast.error(t('bridge.mainnetTokenOnly'));
            return;
        }
        if (!amountUsdt.trim()) {
            toast.error(t('bridge.needAmount'));
            return;
        }
        if (!isValidBridgeAmount(amountUsdt)) {
            toast.error(t('bridge.minAmount'));
            return;
        }
        setLockingBsc(true);
        try {
            const hash = await lockUsdtForCpcOnBsc({
                from: bscWallet.address,
                tokenAddress: usdtToken,
                escrowAddress: escrowContract,
                cpcRecipient: cpcRecipient.trim(),
                amount: amountUsdt,
                decimals: usdtDecimals,
                bscRpcUrl: bscRpc,
            });
            toast.success(
                t('bridge.lockedUsdtOk')
                    .replace('{amount}', amountUsdt)
                    .replace('{usdt}', USDT_SYMBOL)
                    .replace('{tx}', hash.slice(0, 10)),
            );
            void bscWallet.refreshUsdtBalance();
            goHistory(cpcRecipient.trim());
        } catch (e) {
            toast.error(e instanceof Error ? e.message : String(e));
        } finally {
            setLockingBsc(false);
        }
    };

    if (!config) {
        return (
            <PageLayout
                content={
                    <div className="mx-auto max-w-2xl py-8 sm:py-16 text-center text-white">
                        <Heading variant="heading/h1" className="text-2xl sm:text-3xl font-bold tracking-tight text-white mb-2">
                            DATA ↔ BNB USDT
                        </Heading>
                        <p className="text-gray-400 mb-6 text-sm sm:text-base">{t('bridge.noConfig')}</p>
                        <div className="bg-[#121624] border border-gray-800 rounded-2xl p-4 sm:p-6 text-left shadow-xl">
                            <span className="text-sm font-semibold text-gray-300 block mb-2">{t('bridge.noConfigLocalTitle')}</span>
                            <pre className="overflow-auto rounded-xl bg-[#090D1A] p-3 sm:p-4 font-mono text-xs text-blue-400 border border-gray-900">
                                {`./cpc.sh stack first-time\n# or fix Bridge then\n./cpc.sh client fetch-stack-config`}
                            </pre>
                            <span className="text-xs text-gray-500 mt-3 block">{t('bridge.noConfigSource')}</span>
                        </div>
                        <Link to="/" className="mt-6 sm:mt-8 inline-flex items-center gap-2 text-sm font-medium text-[#38BDF8] hover:text-blue-400 transition">
                            {t('bridge.backHome')}
                        </Link>
                    </div>
                }
            />
        );
    }

    const treasuryCpc = statusQ.data?.treasury != null ? mistToDgc(statusQ.data.treasury) : '—';

    return (
        <PageLayout
            gradient={{
                size: 'md',
                content: (
                    <CpcPageHero
                        title="DATA ↔ BNB USDT Bridge"
                        subtitle={t('bridge.subtitle')}
                        mobileSubtitle={t('bridge.mobileSubtitle')}
                        actions={
                            <div className="flex flex-wrap items-center gap-2">
                                <Link
                                    to={
                                        cpcAccount?.address
                                            ? `/bridge/history?address=${encodeURIComponent(cpcAccount.address)}`
                                            : '/bridge/history'
                                    }
                                    className="inline-flex h-11 items-center rounded-xl bg-white/5 px-4 text-sm font-semibold text-[#AEB9D0] hover:text-white"
                                >
                                    {t('bridge.historyOpen')}
                                </Link>
                                <BscConnectButton
                                    address={bscWallet.address}
                                    connecting={bscWallet.connecting}
                                    hasProvider={bscWallet.hasProvider}
                                    onConnect={() => void bscWallet.connect().catch((e) => toast.error(String(e)))}
                                    onDisconnect={bscWallet.disconnect}
                                />
                            </div>
                        }
                    />
                ),
            }}
            content={
                <div className={`${cpcPageContentClass} pb-4 font-sans text-[#aeb9d0] sm:pb-6 lg:pb-8`}>

                        {/* 网络切换与全局选项 - 移动端优化 */}
                        <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                            <DirectionTabs direction={direction} onChange={setDirection} />
                            {/* <label className="inline-flex items-center gap-2.5 cursor-pointer text-xs sm:text-sm text-gray-400 hover:text-gray-300 transition touch-manipulation">
                                <input
                                    type="checkbox"
                                    className="h-4 w-4 rounded border-gray-700 bg-[#0F172A] text-[#38BDF8] focus:ring-[#38BDF8]/30 focus:ring-offset-0"
                                    checked={useTestnet}
                                    onChange={(e) => setUseTestnet(e.target.checked)}
                                />
                                <span className="font-medium">BSC 测试网 (Chapel 97)</span>
                            </label> */}
                        </div>

                        {/* 主体两栏布局 - 移动端优化 */}
                        <div className="grid gap-4 sm:gap-5 lg:gap-8 lg:grid-cols-12 items-start">
                            
                            {/* 左侧：跨链桥核心控制面板 */}
                            <div className="lg:col-span-7 w-full">
                                <Card bg="dark" spacing="lg" className="relative overflow-hidden">
                                <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#38BDF8] to-[#8B5CF6]" />
                                
                                {direction === 'usdt-to-cpc' ? (
                                    <div className="space-y-4 sm:space-y-5">
                                        <div className="flex flex-wrap justify-between items-baseline gap-2 mb-1">
                                            <h2 className="text-lg sm:text-xl font-semibold text-white">{t('bridge.buyTitle')}</h2>
                                            <span className="text-xs text-gray-400 bg-white/5 px-2 py-1 rounded-lg">{rateLabel}</span>
                                        </div>

                                        <AmountInput
                                            value={amountUsdt}
                                            onChange={setAmountUsdt}
                                            label="From (BNB Smart Chain)"
                                            symbol={USDT_SYMBOL}
                                            balance={bscWallet.usdtBalance}
                                            balanceLabel={t('bridge.balance')}
                                        />

                                        {/* 箭头装饰 - 移动端优化 */}
                                        <div className="flex justify-center -my-1.5 relative z-10">
                                            <div className="bg-[#0F172A] border border-white/10 p-2 rounded-full shadow-lg">
                                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-4 h-4 text-[#38BDF8]">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 13.5 12 21m0 0-7.5-7.5M12 21V3" />
                                                </svg>
                                            </div>
                                        </div>

                                        <AmountInput
                                            value={estimatedCpc}
                                            onChange={() => {}}
                                            label={t('bridge.toReceiveData')}
                                            symbol={BRIDGE_ASSET_SYMBOL}
                                            disabled
                                        />

                                        <RecipientInput
                                            value={cpcRecipient}
                                            onChange={(v) => setCpcRecipient(toCpcDisplayAddress(v))}
                                            label={t('bridge.cpcRecipient')}
                                            placeholder={t('bridge.recipientPlaceholder')}
                                            onUseCurrent={cpcAccount ? () => setCpcRecipient(toCpcDisplayAddress(cpcAccount.address)) : undefined}
                                            currentLabel={t('bridge.currentWallet')}
                                        />

                                        <ActionButton
                                            onClick={onLockUsdtForCpc}
                                            disabled={!bscWallet.address || !cpcRecipient.trim() || useTestnet || !escrowContract || !isValidBridgeAmount(amountUsdt)}
                                            loading={lockingBsc}
                                            loadingText={t('bridge.bscBusy')}
                                        >
                                            {!bscWallet.address ? t('bridge.connectBscFirst') :
                                             useTestnet ? t('bridge.switchMainnet') :
                                             !escrowContract ? t('bridge.escrowMissing') :
                                             t('bridge.lockUsdtToData')
                                                .replace('{amount}', amountUsdt || '—')
                                                .replace('{usdt}', USDT_SYMBOL)}
                                        </ActionButton>
                                    </div>
                                ) : (
                                    <div className="space-y-4 sm:space-y-5">
                                        <div className="flex flex-wrap justify-between items-baseline gap-2 mb-1">
                                            <h2 className="text-lg sm:text-xl font-semibold text-white">{t('bridge.sellTitle')}</h2>
                                            <span className="text-xs text-gray-400 bg-white/5 px-2 py-1 rounded-lg">{rateLabel}</span>
                                        </div>

                                        <AmountInput
                                            value={amountCpc}
                                            onChange={setAmountCpc}
                                            label={t('bridge.fromLockData')}
                                            symbol={BRIDGE_ASSET_SYMBOL}
                                            balance={
                                                dataBalQ.data
                                                    ? Number(mistToDgc(dataBalQ.data.totalMist))
                                                    : null
                                            }
                                            balanceLabel={t('bridge.balance')}
                                        />

                                        {/* 箭头装饰 */}
                                        <div className="flex justify-center -my-1.5 relative z-10">
                                            <div className="bg-[#0F172A] border border-white/10 p-2 rounded-full shadow-lg">
                                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-4 h-4 text-[#38BDF8]">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 13.5 12 21m0 0-7.5-7.5M12 21V3" />
                                                </svg>
                                            </div>
                                        </div>

                                        <AmountInput
                                            value={estimatedUsdt}
                                            onChange={() => {}}
                                            label={t('bridge.toReceiveUsdt')}
                                            symbol={USDT_SYMBOL}
                                            disabled
                                        />

                                        <RecipientInput
                                            value={bscAddress}
                                            onChange={setBscAddress}
                                            label={t('bridge.bscRecipient')}
                                            placeholder="0x..."
                                            onUseCurrent={bscWallet.address ? () => setBscAddress(bscWallet.address!) : undefined}
                                            currentLabel={t('bridge.currentWallet')}
                                        />

                                        <ActionButton
                                            onClick={onLockCpcForUsdt}
                                            disabled={!cpcAccount || submitting || !bscAddress.trim() || !isValidBridgeAmount(amountCpc)}
                                            loading={submitting}
                                            loadingText={t('bridge.cpcBusy')}
                                        >
                                            {!cpcAccount ? t('bridge.connectCpcFirst') :
                                             t('bridge.lockDataToUsdt')
                                                .replace('{amount}', amountCpc || '—')
                                                .replace('{data}', BRIDGE_ASSET_SYMBOL)
                                                .replace('{usdt}', USDT_SYMBOL)}
                                        </ActionButton>
                                    </div>
                                )}
                                </Card>
                            </div>

                            {/* 右侧：网关状态与实时监控面板 - 移动端优化 */}
                            <div className="lg:col-span-5 space-y-4 sm:space-y-5">
                                
                                {/* 状态卡片 - 移动端网格优化 */}
                                <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-1">
                                    
                                    {/* DGC 链桥状态 */}
                                    <Card bg="dark" spacing="md">
                                        <h3 className="text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2">
                                            <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                                            {t('bridge.dataBridgeStatus')}
                                        </h3>
                                        <div className="space-y-2">
                                            <InfoRow 
                                                label={t('bridge.treasuryLocked')} 
                                                value={<span className="font-semibold text-white tabular-nums">{treasuryCpc} {BRIDGE_ASSET_SYMBOL}</span>} 
                                            />
                                            <InfoRow 
                                                label={t('bridge.gatewayRpc')} 
                                                value={<StatusBadge ok={statusQ.data?.ok} loading={statusQ.isLoading} label={t('bridge.detecting')} />} 
                                            />
                                            <InfoRow 
                                                label="Bridge Object" 
                                                value={<span className="font-mono text-xs" title={config.bridgeId}>{config.bridgeId.slice(0, 12)}…</span>} 
                                            />
                                        </div>
                                    </Card>

                                    {/* BSC 状态 */}
                                    <Card bg="dark" spacing="md">
                                        <h3 className="text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2">
                                            <span className={clsx(
                                                'h-2 w-2 rounded-full',
                                                bscWallet.address ? (bscWallet.chainOk ? 'bg-emerald-400 animate-pulse' : 'bg-rose-400') : 'bg-yellow-500'
                                            )} />
                                            {t('bridge.bnbUsdtStatus')}
                                        </h3>
                                        <div className="space-y-2">
                                            <InfoRow label={t('bridge.targetNetwork')} value={<span className="font-medium text-white">{chainLabel}</span>} />
                                            <InfoRow 
                                                label={t('bridge.usdtContract')} 
                                                value={<span className="font-mono text-xs" title={usdtToken}>{usdtToken.slice(0, 10)}…</span>} 
                                            />
                                            <InfoRow 
                                                label={t('bridge.escrow')} 
                                                value={<span className={clsx('font-mono text-xs', escrowContract ? 'text-gray-300' : 'text-rose-400')}>
                                                    {escrowContract ? escrowContract.slice(0, 10) + '…' : t('bridge.configMissing')}
                                                </span>} 
                                            />
                                            <InfoRow 
                                                label={t('bridge.nodeStatus')} 
                                                value={<StatusBadge 
                                                    ok={bscWallet.address ? bscWallet.chainOk : undefined} 
                                                    loading={bscWallet.connecting}
                                                    label={bscWallet.address ? (bscWallet.chainOk ? t('bridge.networkMatch') : t('bridge.switchNetwork')) : t('bridge.notConnected')}
                                                />} 
                                            />
                                        </div>
                                    </Card>
                                </div>

                                {/* 交易流水面板 - 移动端优化 */}
                                <Card bg="dark" spacing="md">
                                    <h3 className="text-sm font-semibold text-gray-300 mb-3 flex items-center justify-between">
                                        <span>{t('bridge.recentLocks')}</span>
                                        <Link
                                            to={
                                                cpcAccount?.address
                                                    ? `/bridge/history?address=${encodeURIComponent(cpcAccount.address)}`
                                                    : '/bridge/history'
                                            }
                                            className="text-xs font-medium text-[#38BDF8] hover:text-blue-400"
                                        >
                                            {t('bridge.historyOpen')} →
                                        </Link>
                                    </h3>
                                    
                                    {eventsQ.isLoading ? (
                                        <div className="flex justify-center py-6">
                                            <svg className="animate-spin h-6 w-6 text-[#38BDF8]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                        </div>
                                    ) : (eventsQ.data?.length ?? 0) === 0 ? (
                                        <p className="text-xs text-gray-500 py-6 text-center">{t('bridge.noLocks')}</p>
                                    ) : (
                                        <div className="overflow-x-auto -mx-1">
                                            <table className="w-full text-[10px] sm:text-[11px] text-gray-300">
                                                <thead>
                                                    <tr className="border-b border-gray-800/50 text-left text-gray-500">
                                                        <th className="pb-2 pr-2 font-medium">Nonce</th>
                                                        <th className="pb-2 pr-2 font-medium">{t('bridge.colAmount')}</th>
                                                        <th className="pb-2 pr-2 font-medium hidden sm:table-cell">{t('bridge.colEstimate')}</th>
                                                        <th className="pb-2 pr-2 font-medium hidden sm:table-cell">{t('bridge.colRecipient')}</th>
                                                        <th className="pb-2 font-medium text-right">{t('bridge.colAction')}</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-800/30">
                                                    {eventsQ.data?.slice(0, 5).map((row: BridgeLockedEventRow) => (
                                                        <tr key={`${row.digest}-${row.nonce}`} className="hover:bg-white/5 transition">
                                                            <td className="py-2 pr-2 tabular-nums text-gray-400">{row.nonce}</td>
                                                            <td className="py-2 pr-2 font-medium text-white tabular-nums">{row.amountCpc}</td>
                                                            <td className="py-2 pr-2 text-[#38BDF8] tabular-nums hidden sm:table-cell">
                                                                {cpcAmountToUsdt(row.amountCpc, rate)}
                                                            </td>
                                                            <td className="py-2 pr-2 max-w-[60px] truncate font-mono text-gray-500 hidden sm:table-cell" title={toCpcDisplayAddress(row.recipientHex)}>
                                                                {toCpcDisplayAddress(row.recipientHex)}
                                                            </td>
                                                            <td className="py-2 text-right">
                                                                <Link
                                                                    to={`/txblock/${row.digest}`}
                                                                    className="text-[#38BDF8] hover:text-blue-400 font-medium text-xs sm:text-sm transition touch-manipulation"
                                                                >
                                                                    {t('bridge.details')}
                                                                </Link>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                            {(eventsQ.data?.length ?? 0) > 5 && (
                                                <p className="text-xs text-gray-500 text-center mt-2">{t('bridge.showRecent5')}</p>
                                            )}
                                        </div>
                                    )}
                                </Card>

                                {/* 刷新按钮 - 移动端优化 */}
                                <button
                                    onClick={refresh}
                                    className="w-full py-2.5 text-xs text-gray-400 hover:text-white transition 
                                               bg-white/5 rounded-xl border border-white/5 touch-manipulation
                                               active:scale-[0.98] active:duration-100"
                                >
                                    🔄 {t('bridge.refreshStatus')}
                                </button>
                            </div>
                        </div>

                </div>
            }
        />
    );
}